/**
 * EventPicker Component
 * =====================
 * Search and select an event from USSA API.
 * Shows recent events (past week) by default.
 */

import React, { useState, useEffect } from 'react';
import { searchEvents, getRecentEvents, sectorToSport } from '../api/ussaApi';

function EventPicker({ selectedEvent, onSelect, season, sector }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [recentEvents, setRecentEvents] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingRecent, setIsLoadingRecent] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('recent'); // 'recent' or 'search'

  // Map sector code to API sport value
  const sport = sectorToSport(sector || 'CC');

  // Load recent events on mount
  useEffect(() => {
    const loadRecentEvents = async () => {
      setIsLoadingRecent(true);
      try {
        const events = await getRecentEvents(season, sport, 7);
        setRecentEvents(events);
      } catch (err) {
        console.error('Failed to load recent events:', err);
        // Don't show error for initial load, just empty list
      } finally {
        setIsLoadingRecent(false);
      }
    };
    
    if (!selectedEvent) {
      loadRecentEvents();
    }
  }, [season, sport, selectedEvent]);

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      // If search is cleared, go back to recent view
      setViewMode('recent');
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    setError(null);
    setViewMode('search');

    try {
      // Add wildcards for partial matching
      const query = searchTerm.includes('*') ? searchTerm : `*${searchTerm}*`;
      const results = await searchEvents(query, season, 30, sport);
      setSearchResults(results);
      
      if (results.length === 0) {
        setError(`No ${sport} events found for "${searchTerm}". Try a different search term.`);
      }
    } catch (err) {
      setError(err.message);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    // Parse date without timezone conversion
    const datePart = dateStr.slice(0, 10);
    const [year, month, day] = datePart.split('-');
    const date = new Date(year, month - 1, day); // month is 0-indexed
    
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'];
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    
    const monthName = monthNames[date.getMonth()];
    const dayName = dayNames[date.getDay()];
    
    return `${monthName} ${parseInt(day, 10)}, ${year} (${dayName})`;
  };

  // Get date category for color coding
  const getDateCategory = (dateStr) => {
    if (!dateStr) return 'unknown';
    const eventDate = new Date(dateStr);
    const now = new Date();
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    
    if (eventDate > now) return 'future';
    if (eventDate >= oneWeekAgo) return 'recent';
    return 'past';
  };

  // Sort results by date (most recent first)
  const sortedResults = [...searchResults].sort((a, b) => {
    const dateA = a.date?.start ? new Date(a.date.start) : new Date(0);
    const dateB = b.date?.start ? new Date(b.date.start) : new Date(0);
    return dateB - dateA;
  });

  // Determine which events to display
  const displayEvents = viewMode === 'search' ? sortedResults : recentEvents;
  const isLoading = viewMode === 'search' ? isSearching : isLoadingRecent;

  return (
    <div className="event-picker">
      {/* Current Selection */}
      {selectedEvent && (
        <div className="current-selection">
          <strong>Selected:</strong> {selectedEvent.name}
          <span className="location">
            {selectedEvent.location}, {selectedEvent.state}
          </span>
          <button 
            className="change-btn"
            onClick={() => onSelect(null)}
          >
            Change
          </button>
        </div>
      )}

      {/* Search */}
      {!selectedEvent && (
        <>
          <div className="search-box">
            <input
              type="text"
              placeholder="Search events (e.g., Anchorage, Nationals...)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isSearching}
            />
            <button onClick={handleSearch} disabled={isSearching}>
              {isSearching ? '...' : '🔍 Search'}
            </button>
            {viewMode === 'search' && searchResults.length > 0 && (
              <button 
                className="clear-search-btn"
                onClick={() => {
                  setSearchTerm('');
                  setSearchResults([]);
                  setViewMode('recent');
                  setError(null);
                }}
              >
                ← Back to Recent
              </button>
            )}
          </div>

          {error && <div className="error-message">{error}</div>}

          {/* Section Header */}
          <div className="events-section-header">
            {viewMode === 'recent' ? (
              <>📅 Recent Events <span className="subtitle">(past 7 days)</span></>
            ) : (
              <>🔍 Search Results <span className="subtitle">({searchResults.length} found)</span></>
            )}
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="loading-message">Loading events...</div>
          )}

          {/* Results Table */}
          {!isLoading && displayEvents.length > 0 && (
            <div className="search-results">
              <table>
                <thead>
                  <tr>
                    <th>Event Name</th>
                    <th>Location</th>
                    <th>Date</th>
                    <th>Races</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {displayEvents.map((event) => (
                    <tr key={event.id || event.code}>
                      <td>{event.name}</td>
                      <td>
                        {event.location}, {event.state}
                      </td>
                      <td className={`date-${getDateCategory(event.date?.start)}`}>
                        {formatDate(event.date?.start)}
                      </td>
                      <td>{event.races?.length || 0}</td>
                      <td>
                        <button
                          className="select-btn"
                          onClick={() => onSelect(event)}
                        >
                          Select
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Empty State */}
          {!isLoading && displayEvents.length === 0 && !error && (
            <div className="empty-message">
              {viewMode === 'recent' 
                ? 'No events found in the past 7 days. Use the search above to find events.'
                : 'No events found. Try a different search term.'}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default EventPicker;

